import { createContext, useContext, useEffect, useMemo, useRef, useState, ReactNode } from "react";
import { TENANT_STATUS } from "@/lib/status-constants";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { AppRole, ModulePermissions, useRolePermissions, RolePermissions } from "@/hooks/useRolePermissions";
import { syncTenantContext, setActiveTenantId } from "@/integrations/supabase/setTenantContext";
import { setSentryUser, clearSentryUser } from "@/lib/sentry";

interface Profile {
  id: string;
  user_id: string;
  full_name: string | null;
  avatar_url: string | null;
  phone: string | null;
  position: string | null;
  email?: string | null;
  force_password_reset?: boolean;
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
}

interface UserRole {
  id: string;
  user_id: string;
  tenant_id: string;
  role: AppRole;
  module_permissions?: ModulePermissions | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  currentTenant: Tenant | null;
  tenants: Tenant[];
  currentRole: AppRole | null;
  userRoles: UserRole[];
  permissions: RolePermissions;
  isLoading: boolean;
  isDataLoaded: boolean;
  hasTenant: boolean;
  isActive: boolean;
  mustChangePassword: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  setCurrentTenant: (tenant: Tenant) => void;
  refreshProfile: () => Promise<void>;
  loadUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [currentTenant, setCurrentTenantState] = useState<Tenant | null>(null);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [currentRole, setCurrentRole] = useState<AppRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isActive, setIsActive] = useState(true);

  // Refs to avoid stale closure comparisons in the onAuthStateChange listener
  const currentUserIdRef = useRef<string | null>(null);
  const isDataLoadedRef = useRef(false);

  const currentModulePermissions = useMemo(
    () => userRoles.find((role) => role.tenant_id === currentTenant?.id)?.module_permissions || null,
    [currentTenant?.id, userRoles],
  );
  const permissions = useRolePermissions(currentRole, currentModulePermissions);
  const hasTenant = tenants.length > 0;
  const mustChangePassword = Boolean(profile?.force_password_reset);

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase.from("profiles").select("*").eq("user_id", userId).maybeSingle();
    if (!error && data) setProfile(data as Profile);
  };

  const fetchUserRoles = async (userId: string) => {
    const { data, error } = await supabase.from("user_roles").select("*").eq("user_id", userId);
    if (!error && data) setUserRoles(data as UserRole[]);
    return (data as UserRole[] | null) || [];
  };

  const fetchTenants = async (userId: string) => {
    const { data, error } = await supabase
      .from("tenant_members")
      .select("tenant:tenants(id, name, slug, logo_url, status), is_active")
      .eq("user_id", userId);

    if (!error && data) {
      // Filter out suspended/cancelled tenants
      const activeTenants = data
        .filter((tm: any) => {
          const status = tm.tenant?.status;
          return status === TENANT_STATUS.ACTIVE || status === TENANT_STATUS.TRIAL;
        })
        .map((tm: any) => tm.tenant)
        .filter(Boolean) as Tenant[];
      
      const hasAnyActiveMembership = data.some((tm: any) => tm.is_active === true);
      setIsActive(hasAnyActiveMembership);
      setTenants(activeTenants);
      return activeTenants;
    }

    return [];
  };

  const setCurrentTenant = (tenant: Tenant) => {
    setCurrentTenantState(tenant);
    const roleForTenant = userRoles.find((r) => r.tenant_id === tenant.id);
    setCurrentRole(roleForTenant?.role || null);
    localStorage.setItem("currentTenantId", tenant.id);
    setActiveTenantId(tenant.id);
    syncTenantContext(tenant.id);
  };

  const loadUserData = async (userId: string, silent = false) => {
    try {
      if (!silent) {
        setIsDataLoaded(false);
        isDataLoadedRef.current = false;
      }
      const [_, roles, tenantsList] = await Promise.all([
        fetchProfile(userId),
        fetchUserRoles(userId),
        fetchTenants(userId),
      ]);

      const savedTenantId = localStorage.getItem("currentTenantId");
      let selectedTenant: Tenant | null = null;

      if (savedTenantId && tenantsList.length > 0) {
        selectedTenant = tenantsList.find((t) => t.id === savedTenantId) || tenantsList[0];
      } else if (tenantsList.length > 0) {
        selectedTenant = tenantsList[0];
      }

      if (selectedTenant) {
        setCurrentTenantState(selectedTenant);
        const roleForTenant = roles.find((r) => r.tenant_id === selectedTenant!.id);
        setCurrentRole(roleForTenant?.role || null);
        setActiveTenantId(selectedTenant.id);
        syncTenantContext(selectedTenant.id);
      }

      currentUserIdRef.current = userId;
      setIsDataLoaded(true);
      isDataLoadedRef.current = true;
      setSentryUser(userId, session?.user?.email);
    } catch (error) {
      console.error("Error loading user data:", error);
      const Sentry = await import("@sentry/react");
      Sentry.captureException(error, { tags: { module: "auth" }, extra: { userId } });
      setIsDataLoaded(true);
      isDataLoadedRef.current = true;
    }
  };

  const refreshProfile = async () => {
    if (user) await fetchProfile(user.id);
  };

  const reloadUserData = async () => {
    if (user) await loadUserData(user.id);
  };

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) await loadUserData(session.user.id);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!isMounted) return;

      // TOKEN_REFRESHED: do nothing — token is updated internally by Supabase
      if (_event === 'TOKEN_REFRESHED') {
        return;
      }

      // INITIAL_SESSION: already handled by initializeAuth() above — skip to avoid double bootstrap
      if (_event === 'INITIAL_SESSION') {
        return;
      }

      if (_event === 'SIGNED_IN') {
        // If same user is already loaded, skip full rebootstrap (happens on tab refocus)
        if (session?.user && session.user.id === currentUserIdRef.current && isDataLoadedRef.current) {
          // Just update session ref silently (no state change = no re-render)
          return;
        }
        setSession(session);
        setUser(session?.user ?? null);
      if (session?.user) {
          loadUserData(session.user.id);
          // Track last sign-in silently
          supabase.from("profiles").update({ last_sign_in_at: new Date().toISOString() } as any).eq("user_id", session.user.id).then(() => {});
          // Log login event for admin analytics (fire-and-forget)
          supabase.functions.invoke('log-user-login').catch(() => {});
        }
      } else if (_event === 'SIGNED_OUT') {
        clearSentryUser();
        setSession(null);
        setUser(null);
        setProfile(null);
        setTenants([]);
        setCurrentTenantState(null);
        setUserRoles([]);
        setCurrentRole(null);
        setIsDataLoaded(false);
        isDataLoadedRef.current = false;
        currentUserIdRef.current = null;
        setActiveTenantId(null);
        localStorage.removeItem("currentTenantId");
      } else if (_event === 'USER_UPDATED') {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          fetchProfile(session.user.id);
        }
      }
    });

    initializeAuth();
    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: window.location.origin, data: { full_name: fullName } },
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error: error as Error | null };
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        currentTenant,
        tenants,
        currentRole,
        userRoles,
        permissions,
        isLoading,
        isDataLoaded,
        hasTenant,
        isActive,
        mustChangePassword,
        signIn,
        signUp,
        signOut,
        resetPassword,
        setCurrentTenant,
        refreshProfile,
        loadUserData: reloadUserData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error("useAuth must be used within an AuthProvider");
  return context;
}
